import { SelectableWithIdObject } from "src/components/Form/types";
import { Role, mapRole } from "./roles";
import request from "./request";

interface PositionResponse {
  id: number;
  name: string;
  status?: string;
  role?: Role;
}

export class Position extends SelectableWithIdObject {
  public name: string;
  public status?: string;
  public role?: Role;

  constructor(data: PositionResponse) {
    super(data.id);
    this.name = data.name;
    this.status = data.status;
    if (data.role) {
      this.role = mapRole(data.role);
    }
  }

  getDisplayName(): string {
    return this.name;
  }
}

export const mapPosition = (data: PositionResponse): Position => {
  return new Position(data);
};

export const getPositions = async (): Promise<Position[]> => {
  const response = await request(`/positions`);
  return response.map((data: PositionResponse) => mapPosition(data));
};

export const getPositionsActive = async (): Promise<Position[]> => {
  const response = await request(`/positions/active`);
  return response.map((data: PositionResponse) => mapPosition(data));
};

export const getPositionById = async (positionId: number): Promise<Position> => {
  const response = await request(`/positions/${positionId}`);
  return mapPosition(response);
};

export const mapPositionsByName = (positionNames: string[] = [], allPositions: Position[] = []): Position[] => {
  return positionNames
    .map((name) => allPositions.find((position) => position.name === name))
    .filter(Boolean) as Position[];
};

/*
 * Position writes are deliberately disabled.
 *
 * A position is not a Crew-only entity: the same position also has to be created, renamed,
 * deactivated or deleted on the OPS side. Nothing synchronises the two, so a change made from this
 * dashboard alone leaves Crew and OPS disagreeing about which positions exist - which is why the
 * add/edit/delete UI was removed and these calls were switched off rather than left reachable.
 *
 * Position changes now go through a developer, who applies them on both sides. The implementations
 * are kept verbatim so they can be restored once an OPS-side counterpart exists. To re-enable:
 * uncomment this block, restore `import { BasicResponse } from "./base";` at the top of the file,
 * and bring the controls back in src/features/positions-table.
 *
 * export type PositionRequest = {
 *   name: string;
 *   roleId: number;
 * };
 *
 * export const addPosition = async (positionName: string, roleId: number): Promise<BasicResponse> => {
 *   const response = await request(`/positions`, "POST", {
 *     name: positionName,
 *     roleId: roleId,
 *   });
 *
 *   return response;
 * };
 *
 * export const updatePosition = async (positionId: number, newPosition: PositionRequest): Promise<BasicResponse> => {
 *   const response = await request(`/positions/${positionId}`, "PUT", newPosition);
 *
 *   return response;
 * };
 *
 * export const deletePositionById = async (positionId: number): Promise<BasicResponse> => {
 *   const json = await request(`/positions/${positionId}`, "DELETE");
 *
 *   return json;
 * };
 */
